// skyhawks-peninsula-internal-web/src/components/Coach/CoachTierProgress.js

import React, { useState, useEffect } from 'react';
import Select from 'react-select';
import { getCoachTierDetail, getMyTierDetail } from '../../api/tiers';
import { listAllCoachUsers } from '../../api/admin';
import Loader from '../common/Loader';
import './CoachTierProgress.css';

const SEASON_ORDER = ['winter', 'spring', 'summer', 'fall'];
const FIRST_SEASON = { year: 2026, season: 'summer' };

const getSeasonIndex = (season) => SEASON_ORDER.indexOf(season);

const isSeasonValid = (year, season) => {
  if (year > FIRST_SEASON.year) return true;
  if (year < FIRST_SEASON.year) return false;
  return getSeasonIndex(season) >= getSeasonIndex(FIRST_SEASON.season);
};

const getValidSeasons = (year) => {
  const allSeasons = [
    { value: 'winter', label: 'Winter' },
    { value: 'spring', label: 'Spring' },
    { value: 'summer', label: 'Summer' },
    { value: 'fall', label: 'Fall' },
  ];
  return allSeasons.filter(s => isSeasonValid(year, s.value));
};

const YEARS = [2026, 2027, 2028].map(y => ({ value: y, label: String(y) }));

const getCurrentSeason = () => {
  const month = new Date().getMonth();
  if (month >= 2 && month <= 4) return 'spring';
  if (month >= 5 && month <= 7) return 'summer';
  if (month >= 8 && month <= 10) return 'fall';
  return 'winter';
};

const getCurrentYear = () => new Date().getFullYear();

const getDefaultSelection = () => {
  const currentYear = getCurrentYear();
  const currentSeason = getCurrentSeason();
  if (isSeasonValid(currentYear, currentSeason)) {
    return { year: currentYear, season: currentSeason };
  }
  return FIRST_SEASON;
};

const TIER_REQUIREMENTS = {
  VARSITY: { minPrograms: 2, minSignals: 1, endorsement: 'recommend', bonusPercent: '3%', bonusCap: '$50' },
  ALL_STAR: { minPrograms: 4, minSignals: 2, endorsement: 'strongly-recommend', bonusPercent: '10%', bonusCap: '$150' },
};

const getNextTier = (currentTier) => {
  if (currentTier === 'jv') return 'varsity';
  if (currentTier === 'varsity') return 'all-star';
  return null;
};

const formatEndorsement = (level) => {
  if (!level) return 'None';
  return level.split('-').map(w => w.charAt(0).toUpperCase() + w.slice(1)).join(' ');
};

const getEligibilityStatus = (summary, requirements) => {
  const allStarReqs = requirements.ALL_STAR;
  const varsityReqs = requirements.VARSITY;

  const meetsAllStar =
    summary.programsCompleted >= allStarReqs.minPrograms &&
    summary.positiveSignalsCount >= allStarReqs.minPositiveSignals &&
    summary.complaintsCount === 0;

  const meetsVarsity =
    summary.programsCompleted >= varsityReqs.minPrograms &&
    summary.positiveSignalsCount >= varsityReqs.minPositiveSignals &&
    summary.complaintsCount === 0;

  if (meetsAllStar) {
    return { tier: 'All-Star', met: true };
  } else if (meetsVarsity) {
    return { tier: 'Varsity', met: true };
  } else {
    return { tier: 'Varsity', met: false };
  }
};

function CoachTierProgress({ isAdmin, selectedCoachId }) {
  const defaultSelection = getDefaultSelection();
  const [coaches, setCoaches] = useState([]);
  const [selectedCoach, setSelectedCoach] = useState(null);
  const [year, setYear] = useState(YEARS.find(y => y.value === defaultSelection.year));
  const [season, setSeason] = useState({ value: defaultSelection.season, label: defaultSelection.season.charAt(0).toUpperCase() + defaultSelection.season.slice(1) });
  const [availableSeasons, setAvailableSeasons] = useState(getValidSeasons(defaultSelection.year));
  const [tierData, setTierData] = useState(null);
  const [loading, setLoading] = useState(false);
  const [loadingCoaches, setLoadingCoaches] = useState(true);

  useEffect(() => {
    if (isAdmin) {
      setLoadingCoaches(true);
      listAllCoachUsers(false).then(res => {
        if (res?.data) {
          const options = res.data
            .sort((a, b) => `${a.firstName} ${a.lastName}`.localeCompare(`${b.firstName} ${b.lastName}`))
            .map(c => ({ value: c.id, label: `${c.firstName} ${c.lastName}` }));
          setCoaches(options);

          if (selectedCoachId) {
            const preselected = options.find(c => c.value === selectedCoachId);
            if (preselected) {
              setSelectedCoach(preselected);
            }
          }
        }
        setLoadingCoaches(false);
      });
    }
  }, [isAdmin, selectedCoachId]);

  useEffect(() => {
    if (isAdmin && coaches.length > 0 && selectedCoachId) {
      const preselected = coaches.find(c => c.value === selectedCoachId);
      if (preselected && preselected.value !== selectedCoach?.value) {
        setSelectedCoach(preselected);
      }
    }
  }, [selectedCoachId, coaches, isAdmin, selectedCoach?.value]);

  useEffect(() => {
    const newSeasons = getValidSeasons(year.value);
    setAvailableSeasons(newSeasons);
    if (!newSeasons.find(s => s.value === season.value)) {
      setSeason(newSeasons[0]);
    }
  }, [year.value]);

  useEffect(() => {
    // Admin path: requires a coach to be picked from the selector.
    if (isAdmin) {
      if (!selectedCoach) {
        setTierData(null);
        return;
      }
      setLoading(true);
      getCoachTierDetail(selectedCoach.value, year.value, season.value)
        .then(res => {
          if (res?.data) setTierData(res.data);
        })
        .finally(() => setLoading(false));
      return;
    }

    // Coach path: always load the authenticated coach's own progress.
    // The backend derives identity from the token, so no coach id is sent.
    setLoading(true);
    getMyTierDetail(year.value, season.value)
      .then(res => {
        if (res?.data) setTierData(res.data);
      })
      .finally(() => setLoading(false));
  }, [isAdmin, selectedCoach, year, season]);

  const renderProgressBar = (current, target, met) => {
    const percent = Math.min((current / target) * 100, 100);
    return (
      <div className="progress-bar">
        <div className={`progress-fill ${met ? 'met' : ''}`} style={{ width: `${percent}%` }} />
      </div>
    );
  };

  const renderContent = () => {
    if (isAdmin && !selectedCoach) {
      return (
        <div className="tier-empty-state">
          <p>Select a coach to view their tier progress.</p>
        </div>
      );
    }

    if (loading) {
      return <Loader size={60} />;
    }

    if (!tierData) {
      return (
        <div className="tier-empty-state">
          <p>No tier data available for this coach.</p>
        </div>
      );
    }

    const { summary, tier, requirements } = tierData;
    const eligibility = getEligibilityStatus(summary, requirements);
    const hasEndorsement = !!summary.endorsementLevel;
    const isSeasonComplete = hasEndorsement;

    const varsityReqs = requirements.VARSITY;
    const allStarReqs = requirements.ALL_STAR;
    // If eligible for Varsity, show All-Star requirements (that's what they're working toward next)
    const targetReqs = eligibility.met ? allStarReqs : varsityReqs;

    const programsMet = summary.programsCompleted >= targetReqs.minPrograms;
    const signalsMet = summary.positiveSignalsCount >= targetReqs.minPositiveSignals;
    const hasComplaint = summary.complaintsCount > 0;

    return (
      <div className="tier-progress-card">
        {/* Zone 1: Your Progress / Season Results */}
        <div className="zone-progress">
          <div className="zone-header">
            <h3>{isSeasonComplete ? 'Season Results' : 'Your Progress'}</h3>
          </div>

          {/* Eligibility Badge */}
          <div className="eligibility-badge-container">
            {isSeasonComplete ? (
              <div className="eligibility-badge eligibility-final">
                <span className="eligibility-label">Final Tier</span>
                <span className={`tier-badge tier-${tier}`}>{tier.toUpperCase()}</span>
              </div>
            ) : eligibility.met && eligibility.tier === 'All-Star' ? (
              <div className="eligibility-badge eligibility-met">
                <span className="eligibility-text">Eligible for</span>
                <span className="tier-badge tier-all-star">ALL-STAR</span>
                <span className="eligibility-text">consideration</span>
              </div>
            ) : eligibility.met && eligibility.tier === 'Varsity' ? (
              <div className="eligibility-badge eligibility-achieved-working">
                <div className="eligibility-achieved">
                  <span className="eligibility-text">Eligible for</span>
                  <span className="tier-badge tier-varsity">VARSITY</span>
                  <span className="eligibility-text">consideration</span>
                  <span className="eligibility-check">✓</span>
                </div>
                <div className="eligibility-next">
                  <span className="eligibility-text">Working toward</span>
                  <span className="tier-badge tier-all-star">ALL-STAR</span>
                </div>
              </div>
            ) : tier === 'varsity' ? (
              <div className="eligibility-badge eligibility-achieved-working">
                <div className="eligibility-achieved">
                  <span className={`tier-badge tier-varsity`}>VARSITY</span>
                  <span className="eligibility-check">✓</span>
                </div>
                <div className="eligibility-next">
                  <span className="eligibility-text">Working toward</span>
                  <span className={`tier-badge tier-all-star`}>ALL-STAR</span>
                </div>
              </div>
            ) : (
              <div className="eligibility-badge eligibility-working">
                <span className="eligibility-text">Working toward</span>
                <span className={`tier-badge tier-${eligibility.tier.toLowerCase().replace(' ', '-')}`}>{eligibility.tier.toUpperCase()}</span>
              </div>
            )}
          </div>

          {/* Metrics Stack */}
          <div className="metrics-stack">
            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Programs Completed</span>
                <span className="metric-value">{summary.programsCompleted} of {targetReqs.minPrograms} needed</span>
                <span className={`metric-check ${programsMet ? 'met' : ''}`}>{programsMet ? '✓' : ''}</span>
              </div>
              {renderProgressBar(summary.programsCompleted, targetReqs.minPrograms, programsMet)}
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Positive Signals</span>
                <span className="metric-value">{summary.positiveSignalsCount} of {targetReqs.minPositiveSignals} needed</span>
                <span className={`metric-check ${signalsMet ? 'met' : ''}`}>{signalsMet ? '✓' : ''}</span>
              </div>
              {renderProgressBar(summary.positiveSignalsCount, targetReqs.minPositiveSignals, signalsMet)}
            </div>

            <div className="metric-card">
              <div className="metric-header">
                <span className="metric-label">Complaints</span>
                <span className={`metric-status ${hasComplaint ? 'status-warning' : 'status-clear'}`}>
                  {hasComplaint ? `${summary.complaintsCount} on record` : 'None'}
                </span>
                <span className={`metric-check ${hasComplaint ? 'not-met' : 'met'}`}>{hasComplaint ? '✗' : '✓'}</span>
              </div>
              {hasComplaint && (
                <p className="complaint-note">If you believe this is a mistake, please reach out to Alanis</p>
              )}
            </div>
          </div>
        </div>

        {/* Zone 2: Manager Recommendation */}
        <div className="zone-recommendation">
          {isSeasonComplete ? (
            <>
              <h4 className="recommendation-header">
                Manager Recommendation: <strong>{formatEndorsement(summary.endorsementLevel)}</strong>
              </h4>
              <p className="recommendation-status">Completed</p>
            </>
          ) : (
            <>
              <h4 className="recommendation-header">Manager Recommendation Required</h4>
              <p className="recommendation-body">
                At the end of the season, your manager will decide whether to recommend you for this tier - based on communication, reliability, and how you've worked together.
              </p>
            </>
          )}
        </div>

        {/* Bonus Info */}
        <div className="tier-info-box">
          <div className="tier-info-row">
            <span className="tier-badge tier-varsity">VARSITY</span>
            <span>{TIER_REQUIREMENTS.VARSITY.bonusPercent} bonus (up to {TIER_REQUIREMENTS.VARSITY.bonusCap})</span>
          </div>
          <div className="tier-info-row">
            <span className="tier-badge tier-all-star">ALL-STAR</span>
            <span>{TIER_REQUIREMENTS.ALL_STAR.bonusPercent} bonus (up to {TIER_REQUIREMENTS.ALL_STAR.bonusCap})</span>
          </div>
        </div>

        <p className="tier-footer">Questions? Talk to Alanis.</p>
      </div>
    );
  };

  return (
    <div className="coach-tier-progress">
      <div className="tier-selectors">
        {isAdmin && (
          <div className="selector-group">
            <label>Coach</label>
            <Select
              value={selectedCoach}
              onChange={setSelectedCoach}
              options={coaches}
              placeholder="Select a coach..."
              isLoading={loadingCoaches}
              className="tier-select"
            />
          </div>
        )}
        <div className="selector-group">
          <label>Season</label>
          <Select
            value={season}
            onChange={setSeason}
            options={availableSeasons}
            className="tier-select tier-select-small"
          />
        </div>
        <div className="selector-group">
          <label>Year</label>
          <Select
            value={year}
            onChange={setYear}
            options={YEARS}
            className="tier-select tier-select-small"
          />
        </div>
      </div>

      {renderContent()}
    </div>
  );
}

export default CoachTierProgress;
